/**
 * WalletLockContext — Session-Based Wallet Security Gate
 *
 * Manages the "Wallet Lock" state:
 * - `isWalletUnlocked`: stored in sessionStorage (dies when the tab closes).
 * - `hasPinSetup`: fetched from Firestore on mount (does the user have a PIN?).
 * - Provides `setupPin()` and `verifyPin()` methods that call Cloud Functions.
 *
 * Any page/component can check `useWalletLock()` to gate financial actions.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { callSetWalletPin, callVerifyWalletPin, checkHasPinSetup } from '../services/walletService';
import PinKeypad from '../components/PinKeypad';
import { useModal } from './ModalContext';
import { Lock, X } from 'lucide-react';

const SESSION_KEY = 'tournahub_wallet_unlocked';

interface WalletLockContextType {
    isWalletUnlocked: boolean;
    hasPinSetup: boolean | null;
    checking: boolean;
    setupPin: (pin: string) => Promise<void>;
    verifyPin: (pin: string) => Promise<void>;
    lockWallet: () => void;
    /** Ensure wallet is unlocked before executing action. Opens modal if locked. */
    requireUnlock: (action: () => void) => void;
}

const WalletLockContext = createContext<WalletLockContextType | null>(null);

export function useWalletLock() {
    const ctx = useContext(WalletLockContext);
    if (!ctx) throw new Error('useWalletLock must be used within WalletLockProvider');
    return ctx;
}

export function WalletLockProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const { showAlert } = useModal();

    const [isWalletUnlocked, setIsWalletUnlocked] = useState<boolean>(() => {
        try {
            return sessionStorage.getItem(SESSION_KEY) === 'true';
        } catch {
            return false;
        }
    });

    const [hasPinSetup, setHasPinSetup] = useState<boolean | null>(null);
    const [checking, setChecking] = useState(true);

    // ── Global PIN Modal State ──
    const [showGlobalModal, setShowGlobalModal] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const [pinError, setPinError] = useState('');

    useEffect(() => {
        if (!user) {
            setHasPinSetup(null);
            setChecking(false);
            setIsWalletUnlocked(false);
            try { sessionStorage.removeItem(SESSION_KEY); } catch { /* noop */ }
            return;
        }

        let cancelled = false;
        setChecking(true);

        checkHasPinSetup(user.uid).then((hasPin) => {
            if (!cancelled) {
                setHasPinSetup(hasPin);
                setChecking(false);
            }
        }).catch(() => {
            if (!cancelled) {
                setHasPinSetup(false);
                setChecking(false);
            }
        });

        return () => { cancelled = true; };
    }, [user]);

    useEffect(() => {
        try {
            if (isWalletUnlocked) {
                sessionStorage.setItem(SESSION_KEY, 'true');
            } else {
                sessionStorage.removeItem(SESSION_KEY);
            }
        } catch { /* noop — private browsing */ }
    }, [isWalletUnlocked]);

    const setupPin = useCallback(async (pin: string) => {
        await callSetWalletPin(pin);
        setHasPinSetup(true);
        setIsWalletUnlocked(true);
    }, []);

    const verifyPin = useCallback(async (pin: string) => {
        await callVerifyWalletPin(pin);
        setIsWalletUnlocked(true);
    }, []);

    const lockWallet = useCallback(() => {
        setIsWalletUnlocked(false);
    }, []);

    // ── Central Gate Function ──
    const requireUnlock = useCallback((action: () => void) => {
        if (hasPinSetup === false) {
            showAlert('Please set up your Wallet PIN first by visiting your Wallet page.', 'Wallet PIN Required');
            return;
        }
        if (!isWalletUnlocked) {
            setPendingAction(() => action);
            setShowGlobalModal(true);
            return;
        }
        action();
    }, [hasPinSetup, isWalletUnlocked, showAlert]);

    // Handle PIN submission from the global modal
    const handlePinSubmit = async (pin: string) => {
        setPinError('');
        try {
            await verifyPin(pin);
            setShowGlobalModal(false);
            if (pendingAction) {
                pendingAction();
                setPendingAction(null);
            }
        } catch (error: any) {
            setPinError(error.message || 'Incorrect PIN');
        }
    };

    return (
        <WalletLockContext.Provider value={{
            isWalletUnlocked,
            hasPinSetup,
            checking,
            setupPin,
            verifyPin,
            lockWallet,
            requireUnlock
        }}>
            {children}

            {/* Global PIN Gate Overlay */}
            {showGlobalModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999, padding: 20
                }}>
                    <div className="card animate-in" style={{ maxWidth: 400, width: '100%', position: 'relative' }}>
                        <button className="icon-btn" onClick={() => { setShowGlobalModal(false); setPendingAction(null); setPinError(''); }} style={{ position: 'absolute', right: 16, top: 16 }}>
                            <X size={20} />
                        </button>
                        <div style={{ textAlign: 'center', marginBottom: 24 }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: '50%', background: 'rgba(168,85,247,0.1)',
                                border: '1px solid rgba(168,85,247,0.2)', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', margin: '0 auto 16px'
                            }}>
                                <Lock size={20} style={{ color: 'var(--accent)' }} />
                            </div>
                            <h3 style={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: '1.2rem' }}>Unlock Wallet</h3>
                            <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)' }}>Enter your 6-digit PIN to authorize this action.</p>
                        </div>
                        <PinKeypad onSubmit={handlePinSubmit} error={pinError} />
                    </div>
                </div>
            )}
        </WalletLockContext.Provider>
    );
}

